import { Card, Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";

// Redux & Tools
import { sessionEnd, showSnackbar } from "../../redux/actions";
import { RootState } from "../../redux/reducers";
import { getOne } from "../../fetchs/get";
import getForecastDates from "../../tools/forecast/getForecastDates";

// Components
import Turnover from "../../components/Forecast/Display/Turnover";
import Salaries from "../../components/Forecast/Display/Salaries";
import Fees from "../../components/Forecast/Display/Fees";
import ForecastTotal from "../../components/Forecast/Display/ForecastTotal";
import AnnualForecastTotal from "../../components/Forecast/Display/AnnualForecastTotal";
import ForecastHeader from "../../components/Forecast/Display/ForecastHeader";
import TNS from "../../components/Forecast/Display/TNS";
import Invests from "../../components/Forecast/Display/Invests";
import Company from "../../components/Forecast/Display/Company";
import Subcontract from "../../components/Forecast/Display/Subcontract";
import DGFIP from "../../components/Forecast/Display/DGFIP";

const ForecastDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const params = useParams();
  
  const token = useSelector((state: RootState) => state.user.token);

  // States
  const [error, setError] = useState("");
  const [forecast, setForecast] = useState<any>();
  const [forecastDates, setForecastDates] = useState<string>("");
  const [activeTab, setActiveTab] = useState<string>("Chiffre d'Affaires");
  const [version, setVersion] = useState<number>(0);
  const [official, setOfficial] = useState<boolean>(false);

  // Notification succès
  useEffect(() => {
    if (state === "addFeeSuccess") {
      dispatch(showSnackbar("Frais de fonctionnement créé avec succès", "success"));
    }
  }, [state, dispatch]);

  // Chargement initial du Forecast
  const getForecast = async () => {
    if (token) {
      getOne(token, '/forecast/', params.id ? params.id : "", setForecast,
        () => { dispatch(sessionEnd()); }, 
        setError
      );
    }
  };

  useEffect(() => {
    getForecast();
  }, []);

  // Gestion des versions
  useEffect(() => {
    if (forecast?.versions) {
      const officialVersion = forecast.versions.find((v: any) => v.official);
      if (officialVersion) {
        setVersion(officialVersion.number);
        setOfficial(true);
      } else if (forecast.versions.length > 0) {
        setVersion(forecast.versions[0].number);
      }
    }
  }, [forecast]);

  useEffect(() => {
    const currentVersion = forecast?.versions.find((v: any) => v.number === version);
    if (currentVersion) {
      setOfficial(currentVersion.official);
    }
  }, [version, forecast]);

  // Calcul des dates (colonnes du tableau)
  useEffect(() => {
    const fetchForecastDates = async () => {
      if (forecast) {
        const dates = await getForecastDates(forecast.startDate, forecast.lenght);
        setForecastDates(dates);
      }
    };
    fetchForecastDates();
  }, [forecast]);

  // Configuration des onglets
  const forecastTabs = [
    { title: "Chiffre d'Affaires", component: <Turnover forecastVersion={version} forecastDates={forecastDates} /> },
    { title: "Salariés", component: <Salaries forecastVersion={version} forecastDates={forecastDates} /> },
    { title: "TNS", component: <TNS forecastVersion={version} forecastDates={forecastDates} /> },
    { title: "Frais de fonctionnement", component: <Fees forecastVersion={version} forecastDates={forecastDates} /> },
    { title: "Investissements", component: <Invests forecastVersion={version} forecastDates={forecastDates} /> },
    { title: "Entreprise", component: <Company forecastVersion={version} forecastDates={forecastDates} /> },
    { title: "Sous-traitance", component: <Subcontract forecastVersion={version} forecastDates={forecastDates} /> },
    { title: "DGFIP", component: <DGFIP forecastVersion={version} forecastDates={forecastDates} /> },
    { title: "Totaux", component: <ForecastTotal forecastDates={forecastDates} /> },
    { title: "Total prévisionnel", component: <AnnualForecastTotal forecastName={forecast?.name} forecastVersion={version} /> }
  ];

  return (
    <Box sx={{ flexGrow: 1, bgcolor: "#f1f5f9", minHeight: "100vh" }}>
      <ForecastHeader
        forecast={forecast}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        forecastDates={forecastDates}
        forecastTabs={forecastTabs}
        version={version}
        setVersion={setVersion}
        official={official}
        navigate={navigate}
        dispatch={dispatch}
        setError={setError}
        getForecast={getForecast}
      />

      <Card
        elevation={0}
        component="main"
        sx={{
          mx: { xs: 1, md: 3 },
          my: 2,
          p: { xs: 1, md: 2 },
          borderRadius: "16px",
          bgcolor: "transparent",
          minHeight: "calc(100vh - 200px)",
        }}
      >
        {forecastDates ? (
          // On trouve l'objet tab qui correspond au titre actif
          <Box sx={{ animation: "fadeIn 0.3s ease-in-out" }}>
            {forecastTabs.find(t => t.title === activeTab)?.component}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <Typography variant="body1" color="text.secondary">
              Chargement des données...
            </Typography>
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default ForecastDetails;